import MapView from '@arcgis/core/views/MapView';
import { MapViewInputTask } from '../framework/MapViewInputTask';
import { SurfaceDef } from '../model/SurfaceDef';
import { SurfaceBreakline } from '../model/SurfaceBreakline';
import { GroundPoint } from '../model/GroundPoint';
import { esriGeometryEngine, Graphic, Point } from 'cd-eft/utils/esriTypes';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import Multipoint from '@arcgis/core/geometry/Multipoint';
import SimpleLineSymbol from '@arcgis/core/symbols/SimpleLineSymbol';
import { validNextPointSymbol, invalidNextPointSymbol } from '../resources/esriSymbols';

export class ExtendBreaklineInputTask extends MapViewInputTask<SurfaceBreakline> {
    private readonly surfaceDef: SurfaceDef;
    private breaklineToEdit?: SurfaceBreakline;
    private feedbackLayer: GraphicsLayer = new GraphicsLayer({ title: 'extendFeedbackLayer', listMode: 'hide' });
    private allGroundPoints: GroundPoint[] = [];
    private allGroundPointsMP: Multipoint = new Multipoint();
    private readonly tempBreaklinesLayer: GraphicsLayer = new GraphicsLayer({ title: 'tempBreaklinesLayer' });
    private selectedGroundPoint?: GroundPoint;

    constructor(mapView: MapView, surfaceDef: SurfaceDef) {
        super(mapView);
        this.surfaceDef = surfaceDef;
    }

    protected onSetup(): void {
        super.onSetup();
        this.cursor = 'pointer';

        this.tempBreaklinesLayer.removeAll();

        for (const breakline of this.surfaceDef.breaklines) {
            const graphic = breakline.generateGraphic(this.mapView.spatialReference);
            graphic.symbol = new SimpleLineSymbol({ width: 3, color: 'red' });
            this.tempBreaklinesLayer.add(graphic);
        }

        this.mapView.map.add(this.tempBreaklinesLayer);

        this.allGroundPoints = this.surfaceDef.getAllGroundPoints();
        const multipoint: Multipoint = new Multipoint({ spatialReference: this.mapView.spatialReference });
        for (const point of this.allGroundPoints) {
            multipoint.addPoint(point.generateGeometry(this.mapView.spatialReference));
        }
        this.allGroundPointsMP = multipoint;
        this.mapView.map.add(this.feedbackLayer);
    }

    protected onCleanup(): void {
        super.onCleanup();
        this.mapView.map.remove(this.feedbackLayer);
        this.feedbackLayer.removeAll();
        this.breaklineToEdit = undefined;
        this.selectedGroundPoint = undefined;

        this.mapView.map.remove(this.tempBreaklinesLayer);
        this.tempBreaklinesLayer.removeAll();
    }

    protected async onImmediateClick(e: __esri.ViewImmediateClickEvent): Promise<void> {
        super.onImmediateClick(e);
        const mapPoint = e.mapPoint;
        if (!this.breaklineToEdit) {
            // First click: select breakline
            const hitResult = await this.mapView.hitTest(e, { include: this.mapView.map.layers });
            const graphic = hitResult.results.find((r) => r.graphic?.attributes?.eft_type === 'SurfaceBreakline')
                ?.graphic;
            if (!graphic) return;
            graphic.symbol = new SimpleLineSymbol({ width: 3, color: 'blue' });
            this.breaklineToEdit = graphic.attributes as SurfaceBreakline;
            this.feedbackLayer.add(graphic.clone());
            return;
        }
        // Second click: select next point
        if (!this.selectedGroundPoint) {
            const nearest = this.findNearestGroundPoint(mapPoint);
            if (!nearest) return;
            if (this.handleDuplicatePointSelection(nearest)) return;
            const selectedPoint = nearest.generateGeometry(this.mapView.spatialReference);
            this.drawHighlightVertex(selectedPoint);

            const updated = this.extendBreakline(this.breaklineToEdit, nearest);
            if (await this.surfaceDef.checkBreakLineCrosses(updated, this.mapView)) {
                this.feedbackLayer.removeAll();
                this.selectedGroundPoint = undefined;
                return;
            }

            this.surfaceDef.breaklinesSyncSet.updateItem(this.breaklineToEdit, updated, true);
            this.completeNow(updated);
        }
    }

    private handleDuplicatePointSelection(nearestPoint: GroundPoint): boolean {
        if (!this.breaklineToEdit) return false;

        const isDuplicate = this.breaklineToEdit.groundPoints.some(
            (pt: GroundPoint) => pt.id === nearestPoint.id
        );

        if (isDuplicate) {
            const invalidGraphic = new Graphic({
                geometry: nearestPoint.generateGeometry(this.mapView.spatialReference),
                attributes: { id: nearestPoint.id, type: 'selectedVertex' },
                symbol: invalidNextPointSymbol,
            });

            this.feedbackLayer.add(invalidGraphic);
            setTimeout(() => {
                this.feedbackLayer.remove(invalidGraphic);
            }, 1500);

            return true;
        }

        return false;
    }

    private drawHighlightVertex(point: __esri.Point) {
        this.feedbackLayer.removeAll();
        const graphic = new Graphic({ geometry: point, symbol: validNextPointSymbol });
        graphic.attributes = { type: 'selectedVertex' };
        this.feedbackLayer.add(graphic);
    }

    private findNearestGroundPoint(mapPoint: Point): GroundPoint {
        const findResult = esriGeometryEngine.nearestVertex(this.allGroundPointsMP, mapPoint);
        let result;
        if (findResult.distance < 11) {
            result = this.allGroundPoints[findResult.vertexIndex];
        }
        return result;
    }

    private extendBreakline(breakline: SurfaceBreakline, newPoint: GroundPoint): SurfaceBreakline {
        const updated = this.cloneBreakline(breakline);
        const firstPoint = updated.groundPoints[0].generateGeometry(this.mapView.spatialReference);
        const lastPoint = updated.groundPoints[updated.groundPoints.length - 1].generateGeometry(this.mapView.spatialReference)
        const selectedPoint = newPoint.generateGeometry(this.mapView.spatialReference);

        const distToFirst = esriGeometryEngine.distance(firstPoint, selectedPoint, 'meters');
        const distToLast = esriGeometryEngine.distance(lastPoint, selectedPoint, 'meters');

        if (distToFirst < distToLast) {
            updated.groundPoints.unshift(newPoint);
        } else {
            updated.groundPoints.push(newPoint);
        }
        return updated;
    }

    private cloneBreakline(original: SurfaceBreakline): SurfaceBreakline {
        const newBreakline = new SurfaceBreakline(original.breaklineId);
        newBreakline.groundPoints.push(...original.groundPoints);
        return newBreakline;
    }
}

